import React from 'react';
import { Sparkles, Plus, X, Loader2, Package } from 'lucide-react';
import ProductMatchAutocomplete from '@/components/ProductMatchAutocomplete';

const DOC_TYPES = ['Quote', 'Invoice', 'Order Confirmation', 'Packing Slip', 'Other'];
const UNIT_OPTIONS = ['each', 'ft', 'box', 'spool', 'roll', 'bundle', 'other'];

/**
 * AI-extracted document data editor: header fields, line items (with product
 * autocomplete), totals, and the "Create Order from This" CTA. Behaves as a
 * controlled component driven by the parent page's state.
 */
const ExtractedDataPanel = ({
  parsedFields,
  lineItems,
  parsing,
  subtotal,
  totalAmount,
  isEdited,
  onReParse,
  onFieldChange,
  onLineItemChange,
  onAddLineItem,
  onRemoveLineItem,
  onOpenCreateOrder,
}) => {
  return (
    <div className="bg-white border border-zinc-200 mb-6" data-testid="extracted-data-panel">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-zinc-200 bg-zinc-50">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h2 className="font-heading font-bold text-sm uppercase tracking-wider">Extracted Data</h2>
        </div>
        <button
          onClick={onReParse}
          disabled={parsing}
          data-testid="re-parse-btn"
          className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-500 hover:text-primary disabled:opacity-50"
        >
          {parsing ? 'Parsing...' : 'Re-Parse'}
        </button>
      </div>

      <div className="p-4 sm:p-6 space-y-5">
        {/* Header fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className={isEdited('vendor_name') ? 'border-l-4 border-primary pl-3' : ''}>
            <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-1">Vendor Name</label>
            <input
              type="text"
              value={parsedFields.vendor_name}
              onChange={e => onFieldChange('vendor_name', e.target.value)}
              data-testid="parsed-vendor-name"
              className="w-full h-10 border border-zinc-200 bg-white text-sm px-3 font-medium focus:outline-none focus:border-primary"
            />
          </div>
          <div className={isEdited('document_type') ? 'border-l-4 border-primary pl-3' : ''}>
            <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-1">Document Type</label>
            <select
              value={parsedFields.document_type}
              onChange={e => onFieldChange('document_type', e.target.value)}
              data-testid="parsed-document-type"
              className="w-full h-10 border border-zinc-200 bg-white text-sm px-3 focus:outline-none focus:border-primary"
            >
              {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {['po_number', 'quote_number', 'invoice_number'].map((f) => (
            <div key={f} className={isEdited(f) ? 'border-l-4 border-primary pl-3' : ''}>
              <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-1">
                {f.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </label>
              <input
                type="text"
                value={parsedFields[f]}
                onChange={e => onFieldChange(f, e.target.value)}
                data-testid={`parsed-${f.replace('_', '-')}`}
                className="w-full h-10 border border-zinc-200 bg-white text-sm px-3 font-mono focus:outline-none focus:border-primary"
              />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className={isEdited('order_date') ? 'border-l-4 border-primary pl-3' : ''}>
            <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-1">Order Date</label>
            <input
              type="date"
              value={parsedFields.order_date}
              onChange={e => onFieldChange('order_date', e.target.value)}
              data-testid="parsed-order-date"
              className="w-full h-10 border border-zinc-200 bg-white text-sm px-3 font-mono focus:outline-none focus:border-primary"
            />
          </div>
          <div className={isEdited('expected_delivery_date') ? 'border-l-4 border-primary pl-3' : ''}>
            <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-1">Expected Delivery</label>
            <input
              type="date"
              value={parsedFields.expected_delivery_date}
              onChange={e => onFieldChange('expected_delivery_date', e.target.value)}
              data-testid="parsed-delivery-date"
              className="w-full h-10 border border-zinc-200 bg-white text-sm px-3 font-mono focus:outline-none focus:border-primary"
            />
          </div>
          <div className={isEdited('payment_terms') ? 'border-l-4 border-primary pl-3' : ''}>
            <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-1">Payment Terms</label>
            <input
              type="text"
              value={parsedFields.payment_terms}
              onChange={e => onFieldChange('payment_terms', e.target.value)}
              data-testid="parsed-payment-terms"
              className="w-full h-10 border border-zinc-200 bg-white text-sm px-3 focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        {/* Line Items Table */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400">Line Items</h3>
            <button
              onClick={onAddLineItem}
              data-testid="add-line-item-btn"
              className="text-[10px] font-heading font-semibold uppercase tracking-wider text-primary hover:text-primary-hover flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add Item
            </button>
          </div>

          <div className="border border-zinc-200 overflow-x-auto">
            <table className="w-full min-w-[600px]" data-testid="line-items-table">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400 px-3 py-2 w-[40%]">Description</th>
                  <th className="text-right text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400 px-3 py-2 w-[12%]">Qty</th>
                  <th className="text-left text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400 px-3 py-2 w-[12%]">Unit</th>
                  <th className="text-right text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400 px-3 py-2 w-[15%]">Unit Price</th>
                  <th className="text-right text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400 px-3 py-2 w-[15%]">Extended</th>
                  <th className="px-2 py-2 w-[6%]"></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, idx) => (
                  <tr key={li._key} className="border-b border-zinc-100" data-testid={`line-item-row-${idx}`}>
                    <td className="px-2 py-1">
                      <ProductMatchAutocomplete
                        value={li.product_description}
                        onChange={(v) => {
                          onLineItemChange(idx, 'product_description', v);
                          if (li.product_id) {
                            onLineItemChange(idx, 'product_id', null);
                            onLineItemChange(idx, 'manufacturer_sku', null);
                            onLineItemChange(idx, 'match_confidence', null);
                            onLineItemChange(idx, 'matched_canonical_name', null);
                          }
                        }}
                        onProductSelect={(sel) => {
                          onLineItemChange(idx, 'product_description', sel.product_description);
                          onLineItemChange(idx, 'product_id', sel.product_id);
                          onLineItemChange(idx, 'manufacturer_sku', sel.manufacturer_sku);
                          onLineItemChange(idx, 'match_confidence', sel.match_confidence);
                          onLineItemChange(idx, 'matched_canonical_name', sel.product_description);
                          if (sel.unit_of_measure) onLineItemChange(idx, 'unit', sel.unit_of_measure);
                        }}
                        currentProductId={li.product_id}
                        currentMatchConfidence={li.match_confidence}
                        currentMatchedName={li.matched_canonical_name || li.product_description}
                        placeholder="Description"
                        testIdPrefix={`doc-line-${idx}-product`}
                        inputClassName="w-full h-9 border border-zinc-200 bg-white text-xs px-2 focus:outline-none focus:border-primary"
                      />
                      {li.product_id && li.match_confidence != null && (
                        <div
                          className={`mt-1 inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-wider px-1.5 py-0.5 ${
                            li.match_confidence >= 0.8
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                          data-testid={`doc-line-${idx}-match-tag`}
                          title={`${li.match_confidence >= 0.8 ? 'Matched' : 'Suggested'}: ${li.matched_canonical_name || ''}`}
                        >
                          {li.match_confidence >= 0.8 ? 'Matched' : 'Suggested'}: {(li.matched_canonical_name || '').slice(0, 40)} ({Math.round(li.match_confidence * 100)}%)
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="number"
                        value={li.quantity}
                        onChange={e => onLineItemChange(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-full h-9 border border-zinc-200 bg-white text-xs px-2 text-right font-mono focus:outline-none focus:border-primary"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <select
                        value={li.unit}
                        onChange={e => onLineItemChange(idx, 'unit', e.target.value)}
                        className="w-full h-9 border border-zinc-200 bg-white text-xs px-1 focus:outline-none focus:border-primary"
                      >
                        {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="number"
                        step="0.01"
                        value={li.unit_price}
                        onChange={e => onLineItemChange(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="w-full h-9 border border-zinc-200 bg-white text-xs px-2 text-right font-mono focus:outline-none focus:border-primary"
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <span className="font-mono text-xs text-zinc-700">${li.extended_price.toFixed(2)}</span>
                    </td>
                    <td className="px-1 py-1">
                      <button
                        onClick={() => onRemoveLineItem(idx)}
                        data-testid={`remove-line-item-${idx}`}
                        className="p-1 text-zinc-400 hover:text-destructive"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {lineItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-xs text-zinc-400">No line items. Click "Add Item" above.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-full sm:w-64 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400">Subtotal</span>
              <span className="font-mono font-medium" data-testid="parsed-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <div className={`flex justify-between text-sm items-center ${isEdited('tax') ? 'border-l-4 border-primary pl-2' : ''}`}>
              <span className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400">Tax</span>
              <input
                type="number"
                step="0.01"
                value={parsedFields.tax}
                onChange={e => onFieldChange('tax', parseFloat(e.target.value) || 0)}
                data-testid="parsed-tax"
                className="w-24 h-8 border border-zinc-200 bg-white text-xs px-2 text-right font-mono focus:outline-none focus:border-primary"
              />
            </div>
            <div className={`flex justify-between text-sm items-center ${isEdited('freight') ? 'border-l-4 border-primary pl-2' : ''}`}>
              <span className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400">Freight</span>
              <input
                type="number"
                step="0.01"
                value={parsedFields.freight}
                onChange={e => onFieldChange('freight', parseFloat(e.target.value) || 0)}
                data-testid="parsed-freight"
                className="w-24 h-8 border border-zinc-200 bg-white text-xs px-2 text-right font-mono focus:outline-none focus:border-primary"
              />
            </div>
            <div className="flex justify-between text-sm pt-2 border-t border-zinc-200">
              <span className="font-heading font-bold text-xs uppercase tracking-wider">Total</span>
              <span className="font-mono font-bold" data-testid="parsed-total">${totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Create Order Button */}
        <button
          onClick={onOpenCreateOrder}
          data-testid="create-order-from-parse-btn"
          className="w-full h-14 bg-primary text-black font-heading font-bold uppercase tracking-wider text-sm border border-zinc-200 hover:bg-primary-hover transition-colors flex items-center justify-center gap-3 sticky bottom-4"
        >
          <Package className="w-5 h-5" />
          Create Order from This
        </button>
      </div>
    </div>
  );
};

export default ExtractedDataPanel;
